import React, { useState, useEffect, KeyboardEvent } from 'react';
import ITripPayment from "@/models/trip-payments/itrip-payment";
import DatePickerComponent from "./date-picker";
import * as guestPaymentsService from '@/services/trip-payments-service';
import { toast } from "react-toastify";
import ISelectOption from "@/models/generic/iselect-option";
import fixCurrencyValue from "@/common/fix-currency-values";
import { TripPaymentHasChanged, TripPaymentHasRequiredFormFields } from "./validation";

interface IProps {
    tripPayment: ITripPayment;
    paymentMethods: ISelectOption[];
    close: (tripPayment: ITripPayment, requiresUpdate: boolean) => void;
}

const EditTripPayment: React.FC<IProps> = ({ tripPayment: guestPayment, paymentMethods, close }) => {

    const [ oldGuestPayment, setOldGuestPayment ] = useState<ITripPayment>({} as ITripPayment);
    const [ newGuestPayment, setNewGuestPayment ] = useState<ITripPayment>({} as ITripPayment);

    useEffect(() => {
        try {
            const updateData = async () => {
                if (guestPayment.id > 0)
                {   
                    const response = await guestPaymentsService.getTripPayment(guestPayment.id);
                    
                    setOldGuestPayment(response);
                    setNewGuestPayment(response);
                } else {
                    setOldGuestPayment(guestPayment);
                    setNewGuestPayment(guestPayment);
                }
            }

            updateData();
            
        } catch {
            toast.error("The guests payments couldn't be loaded.");
        }
    }, []);

    async function UpdateGuestPayment() {
        if (TripPaymentHasRequiredFormFields(newGuestPayment) && TripPaymentHasChanged(oldGuestPayment, newGuestPayment)) {
            const payment = {
                ...newGuestPayment,
                amount: fixCurrencyValue(newGuestPayment.amount)
            } as ITripPayment;

            if (newGuestPayment.id < 1) {
                close(payment, false);
                return;
            }

            const add = async () => {
                return await guestPaymentsService.updateTripPayment(payment);
            }

            const response = await toast.promise(
                add(),
                {
                    pending: 'Updating guest payment',
                    success: 'Guest payment updated',
                    error: 'Something went wrong whilst updating the guest payment'
                }
            );

            if (response) {
                close(payment, true);
            }
        } 
    }

    function CancelAddingGuestPayment() {
        close(oldGuestPayment, false);
    }

    function submitForm(event: KeyboardEvent) {
        if (event.keyCode === 13 || event.key === "Enter") {
            UpdateGuestPayment(); 
        }
    }

    return (
        <>
            <td onKeyUp={submitForm}>
                <DatePickerComponent
                    value={newGuestPayment.paymentDate}
                    newValue={(newValue) => setNewGuestPayment({...newGuestPayment, paymentDate: newValue} as ITripPayment)} 
                />
            </td>
            <td onKeyUp={submitForm}>
                <input 
                    type="number" 
                    placeholder="Amount"
                    className="pl-2 w-full"
                    value={newGuestPayment.amount || ""}
                    onChange={(event) => setNewGuestPayment({...newGuestPayment, amount: Number(event.target.value)} as ITripPayment)} 
                />
            </td>
            <td onKeyUp={submitForm}>
                <select 
                    className="w-full px-2"
                    value={newGuestPayment.methodId} 
                    onChange={(event) => setNewGuestPayment({...newGuestPayment, methodId: Number(event.target.value)} as ITripPayment)}
                >
                    {paymentMethods.map(x => {
                        return (
                            <option key={x.id} value={x.id}>
                                {x.name}
                            </option>
                        );
                    })}
                </select>
            </td>
            <td className="text-right" onKeyUp={submitForm}>
                <button title="Save" onClick={() => UpdateGuestPayment()} className="bg-green-500 hover:brightness-110 text-white px-1 w-6"><i className="fa fa-check" /></button>
                <button title="Cancel" onClick={() => CancelAddingGuestPayment()} className="mx-1 bg-red-600 hover:brightness-110 text-white px-1 w-6"><i className="fa fa-times" /></button>
            </td>
        </>
    )
}

export default EditTripPayment;